import { memo, useState, useCallback, useEffect, useContext, useMemo } from 'react'
import styled from 'styled-components'
import moment from 'moment'
import { rem } from 'polished'
import Calendar from 'components/Common/Calendar'
import { addMonths, endOfMonth, dateIsAfter, dateIsBefore, startOfMonth } from 'lib/datetime/dateUtils'
import { CALENDAR_GA_EVENT_ACTION, CALENDAR_GA_EVENT_LABEL } from 'constants/analytics'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import Heading from '../../../../LuxKit/Typography/Heading'
import LineAngleLeftBIcon from 'LuxKit/Icons/line/LineAngleLeftBIcon'
import LineAngleRightBIcon from 'LuxKit/Icons/line/LineAngleRightBIcon'
import useDidChange from 'hooks/useDidChange'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import IconButton from 'LuxKit/Button/IconButton'
import VerticalSpacer from '../Spacing/VerticalSpacer'

const Root = styled.div`
  display: flex;
  width: 100%;

  > * {
    flex-grow: 1;

    & + * {
      margin-left: ${rem(52)};
    }
  }
`

const Header = styled.div`
  display: grid;
  grid-template: "prev-button heading next-button" auto / ${rem(28)} 1fr ${rem(28)};
  place-items: center;
`

const HeaderHeading = styled(Heading)`
  grid-area: heading;
`

const LeftArrow = styled(IconButton)`
  grid-area: prev-button;
`

const RightArrow = styled(IconButton)`
  grid-area: next-button;
`

interface Props {
  onDatesChange: (dates: { startDate: moment.Moment, endDate: moment.Moment | undefined }) => void
  endDate?: moment.Moment
  startDate?: moment.Moment
  initDate?: moment.Moment
  minDate?: Date
  maxDate?: Date
  className?: string
  startLabel?: string
  endLabel?: string
  availableMonths?: Array<string>
  allowSameDayRange?: boolean
}

function DualDateRangePicker(props: Props) {
  const {
    onDatesChange,
    endDate,
    startDate,
    initDate,
    minDate,
    maxDate,
    className,
    startLabel,
    endLabel,
    availableMonths,
    allowSameDayRange,
  } = props
  const [index, setIndex] = useState<number>(0)
  const [date, setDate] = useState<Date>(initDate?.toDate() || startDate?.toDate() || new Date())

  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)

  const onDayClick = useCallback((day: moment.Moment) => {
    const isSameStartDay = startDate && day.isSame(startDate, 'day')
    const shouldSelectEndDate = startDate && !endDate && (day.isAfter(startDate) || (allowSameDayRange && isSameStartDay))

    if (shouldSelectEndDate) {
      onDatesChange({ startDate, endDate: day })
      onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.CheckOut, moment(day).format(ISO_DATE_FORMAT))
    } else {
      onDatesChange({ startDate: day, endDate: undefined })
      onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.CheckIn, moment(day).format(ISO_DATE_FORMAT))
    }
  }, [allowSameDayRange, startDate, endDate, onDatesChange, onUserAnalyticsAction])

  const onNextMonth = useCallback(() => {
    if (availableMonths) {
      if (index >= availableMonths.length - 1) return
      setIndex(index + 1)
    }
    setDate(addMonths(date, 1))
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.ShiftMonth, CALENDAR_GA_EVENT_LABEL.NextMonth)
  }, [availableMonths, date, index, onUserAnalyticsAction])

  const onPrevMonth = useCallback(() => {
    if (availableMonths) {
      if (!index) return
      setIndex(index - 1)
    }
    setDate(addMonths(date, -1))
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.ShiftMonth, CALENDAR_GA_EVENT_LABEL.PreviousMonth)
  }, [availableMonths, date, index, onUserAnalyticsAction])

  const currentDate = useMemo(() => {
    if (availableMonths?.[index]) {
      return moment(availableMonths[index], 'YYYY-MM').toDate()
    }
    return date
  }, [availableMonths, date, index])

  const nextDate = useMemo(() => {
    if (availableMonths?.length) {
      return index < availableMonths.length - 1 ? moment(availableMonths[index + 1], 'YYYY-MM').toDate() : undefined
    }

    return addMonths(date, 1)
  }, [availableMonths, date, index])

  const formattedDate = useCallback((date: Date) => {
    const momentDate = moment(date)
    if (!momentDate.isValid()) return ''
    return `${momentDate.format('MMMM')} ${momentDate.format('YYYY')}`
  }, [])

  const startDateChanged = useDidChange(startDate)
  useEffect(() => {
    if (startDate && startDateChanged &&
      // month on the left === month of the start date
      startDate.month() !== date.getMonth() &&
      // month on the right === month of the start date
      startDate.month() % 12 !== (date.getMonth() + 1) % 12
    ) {
      setDate(startDate.toDate())
    }
  }, [date, startDate, startDateChanged])

  return (<Root className={className}>
    <VerticalSpacer gap={8}>
      <Header>
        <LeftArrow
          kind="tertiary"
          size="small"
          shape="circle"
          horizontalOutdent="start"
          disabled={dateIsBefore(startOfMonth(currentDate), minDate)}
          onClick={onPrevMonth}
        >
          <LineAngleLeftBIcon />
        </LeftArrow>
        <HeaderHeading variant="heading6" align="center">
          {formattedDate(currentDate)}
        </HeaderHeading>
      </Header>
      <Calendar
        month={currentDate.getMonth()}
        year={currentDate.getFullYear()}
        startDate={startDate?.toDate()}
        minDate={minDate}
        maxDate={maxDate}
        endDate={endDate?.toDate()}
        onDayClick={onDayClick}
        startLabel={startLabel}
        endLabel={endLabel}
      />
    </VerticalSpacer>
    <VerticalSpacer gap={8}>
      <Header>
        {!!nextDate && <HeaderHeading variant="heading6" align="center">
          {formattedDate(nextDate)}
        </HeaderHeading>}
        {!!nextDate && <RightArrow
          kind="tertiary"
          size="small"
          shape="circle"
          horizontalOutdent="end"
          data-testid="next-month-calendar"
          disabled={dateIsAfter(endOfMonth(nextDate))}
          onClick={onNextMonth}
        >
          <LineAngleRightBIcon />
        </RightArrow>}
      </Header>
      {!!nextDate && <Calendar
        month={nextDate.getMonth()}
        year={nextDate.getFullYear()}
        startDate={startDate?.toDate()}
        minDate={minDate}
        maxDate={maxDate}
        endDate={endDate?.toDate()}
        onDayClick={onDayClick}
        startLabel={startLabel}
        endLabel={endLabel}
      />}
    </VerticalSpacer>
  </Root>
  )
}

export default memo(DualDateRangePicker)
